import { inject, injectable } from "@launchtray/tsyringe-async";
import { IPaymentProcessingController } from "./IPaymentProcessingController";
import { IPaymentProcessingModel } from "../models/IPaymentProcessingModel";
import { PaymentResult } from "../../../../../web3/PaymentResult";
import { PaymentProcessingPopupSceneOptions } from "../scenes/PaymentProcessingPopupSceneOptions";
import { PaymentProcessingType } from "../type/PaymentProcessingType";
import { CancellationToken } from "../../../../../shared/async/CancellationToken";
import { ILogger } from "../../../../logger/ILogger";
import { DefaultMessagesSceneFactory } from "../../../SceneFactories";
import { IContextResolver } from "../../../../composition/IContextResolver";
import { SceneManager } from "../../../../../scene/SceneManager";
import { Chain } from "thirdweb/chains";
import { PaymentProcessingPopupScene } from "../scenes/PaymentProcessingPopupScene";

@injectable()
export class PaymentProcessingController implements IPaymentProcessingController {
    private readonly erc20PaymentProcessingPopupSceneFactory: (
        options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>,
        cancellationToken: CancellationToken
    ) => Promise<PaymentProcessingPopupScene>;

    public constructor(
        private readonly sceneManager: SceneManager,
        @inject("IPaymentProcessingModel")
        private readonly paymentModel: IPaymentProcessingModel,
        @inject("PaymentProcessingPopupSceneOptions")
        private readonly options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>,
        @inject("IContextResolver")
        private readonly contextResolver: IContextResolver,
        @inject("Erc20PaymentProcessingPopupSceneFactory")
        erc20PaymentProcessingPopupSceneFactory: (
            options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>,
            cancellationToken: CancellationToken
        ) => Promise<PaymentProcessingPopupScene>,
        @inject("ILogger")
        private readonly logger: ILogger
    ) {
        this.erc20PaymentProcessingPopupSceneFactory = erc20PaymentProcessingPopupSceneFactory;
    }

    public async showPaymentProcessingPopup(cancellationToken: CancellationToken): Promise<PaymentResult> {
        return await this.sceneManager.showViewableAsync(await this.erc20PaymentProcessingPopupSceneFactory(this.options, cancellationToken));
    }

    public async buyStoreItem(chain: Chain, erc20Address: string, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            if (this.options.type == PaymentProcessingType.STORE_PACKAGE) {
                const paymentOperation = await this.paymentModel.erc20StorePayment(chain, erc20Address, this.options.tokenId, this.options.price);
                return await this.showPaymentMessage(Promise.resolve(paymentOperation), cancellationToken);
            } else {
                let paymentOperation: PaymentResult;
                if (this.options.reservoirOrderId === null || this.options.reservoirOrderId === undefined) {
                    paymentOperation = await this.paymentModel.purchaseBuildingWithCrypto(
                        chain,
                        this.options.buildingId,
                        this.options.couponBenefitIds?.length > 0 ? this.options.couponBenefitIds : undefined
                    );
                    if (cancellationToken.isCancellationRequested) {
                        return paymentOperation;
                    }
                } else {
                    paymentOperation = await this.paymentModel.buyBuildingThroughReservoir(this.options.reservoirOrderId)
                }
                return await this.showPaymentMessage(Promise.resolve(paymentOperation), cancellationToken);
            }
        } catch (e: unknown) {
            this.logger.error("Exception on store payment: ", e);
            return PaymentResult.FAILURE;
        }
    }

    private async showPaymentMessage(operation: Promise<PaymentResult>, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            const factory: DefaultMessagesSceneFactory = await this.contextResolver.resolve("DefaultMessagesSceneFactory");
            const scene = await factory({ operation, operationCancellationToken: cancellationToken, withProcessingPage: false });
            if (cancellationToken.isCancellationRequested) {
                scene.view.destroy();
                return PaymentResult.CANCELLED;
            }
            return (await this.sceneManager.showViewableAsync(scene)) as PaymentResult;
        } catch (e: unknown) {
            this.logger.error("Exception on showing payment message: ", e);
            return PaymentResult.FAILURE;
        }
    }
}
